import React, { useState } from "react";
import { X, CheckCircle } from "lucide-react";

const SelfAssessmentModal = ({ rubric, onComplete, onClose }) => {
  const [assessments, setAssessments] = useState({});

  // ✅ Lấy danh sách tiêu chí từ rubric (markingRubric của writing_task)
  const criteriaList = Array.isArray(rubric?.criteria)
    ? rubric.criteria.map((item) => ({
      criteria: item.criteria || "",
      weight: item.weight || "",
    }))
    : [];

  // ✅ Mức đánh giá cố định theo CEFR
  const levels = rubric?.levels || ["B1", "B2", "C1", "C2"];

  // ✅ Nếu rubric không hợp lệ → báo lỗi
  if (!criteriaList.length) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 text-center">
          <h2 className="text-lg font-bold text-red-600 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">
            Unable to load assessment rubric. Please try again.
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // ✅ Lưu lựa chọn từng tiêu chí
  const handleSelect = (criterion, level) => {
    setAssessments((prev) => ({
      ...prev,
      [criterion]: level,
    }));
  };

  // ✅ Xử lý khi nhấn Submit
  const handleSubmit = () => {
    if (Object.keys(assessments).length < criteriaList.length) {
      alert("Please assess all criteria before completing.");
      return;
    }
    onComplete(assessments);
  };

  const isComplete =
    Object.keys(assessments).length === criteriaList.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        {/* 🔹 Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800 flex items-center space-x-2">
            <span>📋</span>
            <span>Self-Assessment</span>
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* 🔹 Intro instructions */}
        <div className="px-6 pt-4 pb-2 text-gray-700 text-sm leading-relaxed">
          Please evaluate your own writing performance of this writing task
          based on the basic rubric below. For each criterion, you can choose
          B1 (intermediate), B2 (upper-intermediate), C1 (advanced) or C2
          (proficient) (CEFR) to indicate your estimation of your writing
          performance in that criterion.
        </div>

        {/* 🔹 Rubric table */}
        <div className="p-6 pt-2">
          <div className="overflow-x-auto">
            <table className="w-full border border-gray-300 rounded-lg">
              <thead className="bg-purple-100">
                <tr>
                  <th className="border border-gray-300 p-3 text-left font-semibold min-w-[320px]">
                    Assessment Criteria
                  </th>
                  {levels.map((level) => (
                    <th
                      key={level}
                      className="border border-gray-300 p-3 text-center font-semibold text-purple-700 min-w-[130px]"
                    >
                      {level}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {criteriaList.map((criterion, i) => (
                  <tr
                    key={i}
                    className="odd:bg-white even:bg-purple-50 hover:bg-purple-100 transition"
                  >
                    <td className="border border-gray-300 p-3 text-gray-800 align-top">
                      <p className="font-semibold">{criterion.criteria}</p>
                      {criterion.weight && (
                        <p className="text-sm text-gray-500 italic">({criterion.weight})</p>
                      )}
                    </td>
                    {levels.map((level) => (
                      <td
                        key={level}
                        className="border border-gray-300 p-3 text-center"
                      >
                        <input
                          type="radio"
                          name={`criterion-${i}`}
                          value={level}
                          checked={assessments[criterion.criteria] === level}
                          onChange={() => handleSelect(criterion.criteria, level)}
                          className="w-4 h-4 text-purple-600 focus:ring-purple-500 cursor-pointer"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>

            </table>
          </div>
        </div>

        {/* 🔹 Progress bar */}
        <div className="px-6 pb-6">
          <div className="bg-gray-100 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">
                Assessment Progress:
              </span>
              <span className="text-sm text-gray-600">
                {Object.keys(assessments).length}/{criteriaList.length}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${(Object.keys(assessments).length / criteriaList.length) * 100
                    }%`,
                }}
              />
            </div>
          </div>

          {/* 🔹 Instructions */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-gray-700">
            <p className="font-semibold text-blue-800 mb-2 flex items-center">
              💡 Instructions:
            </p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>
                Read each criterion carefully and select the level that best
                matches your writing.
              </li>
              <li>
                Be honest in your assessment to receive accurate feedback from
                the AI Teacher.
              </li>
              <li>
                After completion, you'll see a comparison between your
                assessment and the AI's.
              </li>
            </ul>
          </div>
        </div>

        {/* 🔹 Footer */}
        <div className="flex justify-end space-x-4 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isComplete}
            className={`px-6 py-2 rounded-lg flex items-center space-x-2 transition-colors ${isComplete
              ? "bg-purple-600 text-white hover:bg-purple-700 cursor-pointer"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
          >
            <CheckCircle className="w-5 h-5" />
            <span>Complete Assessment</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelfAssessmentModal;
